import axios from "axios";

// Always call the backend on the same origin that served the frontend.
// This means bidorah.com → bidorah.com/api, preview.emergentagent.com → same host, etc.
// Falls back to REACT_APP_BACKEND_URL for non-browser environments (SSR, tests).
export const SITE_ORIGIN =
  (typeof window !== "undefined" && window.location && window.location.origin) ||
  process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${SITE_ORIGIN}/api`;

// PUBLIC_HOST is the canonical brand URL we use for SHAREABLE links —
// invite codes, social shares, marketing copy. We never want a preview
// pod's URL leaking into a tweet, so this falls back to bidorah.com when
// no explicit override is set.
//
// Override priority: REACT_APP_PUBLIC_HOST env  →  bidorah.com.
export const PUBLIC_HOST =
  (process.env.REACT_APP_PUBLIC_HOST || "https://bidorah.com").replace(/\/$/, "");

// Build a SHAREABLE link that always reads as bidorah.com regardless of the
// host serving the app (preview pods, localhost, native WebView). Pass any
// URL or path — if it's missing or already points at our preview/native
// origin, we rewrite the scheme+host to PUBLIC_HOST while keeping path,
// query, and hash intact. Used by the live-stream ShareSheet, referral
// link copy buttons, collector profile share, etc.
export function brandUrl(input) {
  // Default to whatever the user is currently looking at.
  const fallback =
    (typeof window !== "undefined" && window.location && window.location.href) || "/";
  let u;
  try {
    u = new URL(input || fallback, fallback);
  } catch (_) {
    return PUBLIC_HOST;
  }
  // Stitch path + query + hash onto the public host.
  return `${PUBLIC_HOST}${u.pathname}${u.search}${u.hash}`;
}

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// ─── Mobile / Capacitor token persistence ────────────────────────────
// Android WebViews routinely drop SameSite=None cookies between app
// launches, which logs the user out every time they reopen the app.
// To dodge this, we ALSO stash the JWT pair in localStorage on login
// and attach `Authorization: Bearer <access>` on every request. The
// backend's `get_current_user` already accepts this header as a
// fallback to the cookie, so the two paths overlap cleanly.

const LS_ACCESS = "bidorah_access_token";
const LS_REFRESH = "bidorah_refresh_token";

const safeStorage = (() => {
  try {
    if (typeof window !== "undefined" && window.localStorage) return window.localStorage;
  } catch (_) { /* private mode */ }
  return null;
})();

// Native key-value store (Android SharedPreferences). Survives WebView
// data wipes that occasionally nuke localStorage on app upgrades / OS
// updates / "clear cache" — the #1 source of the "I got logged out
// again" complaint on the Capacitor APK. We use it as a write-through
// fallback: every save also writes to Preferences; on app boot we
// rehydrate localStorage from Preferences if it's empty.
let _prefsPromise = null;
const getNativePrefs = async () => {
  try {
    if (!_prefsPromise) {
      const { Capacitor } = await import("@capacitor/core");
      if (!Capacitor.isNativePlatform()) return null;
      _prefsPromise = import("@capacitor/preferences").then((m) => m.Preferences);
    }
    return await _prefsPromise;
  } catch (_) {
    return null;
  }
};

export const saveAuthTokens = ({ access_token, refresh_token } = {}) => {
  if (safeStorage) {
    if (access_token) safeStorage.setItem(LS_ACCESS, access_token);
    if (refresh_token) safeStorage.setItem(LS_REFRESH, refresh_token);
  }
  // Mirror into native Preferences (fire-and-forget).
  getNativePrefs().then((p) => {
    if (!p) return;
    if (access_token) p.set({ key: LS_ACCESS, value: access_token }).catch(() => {});
    if (refresh_token) p.set({ key: LS_REFRESH, value: refresh_token }).catch(() => {});
  });
};

export const clearAuthTokens = () => {
  if (safeStorage) {
    safeStorage.removeItem(LS_ACCESS);
    safeStorage.removeItem(LS_REFRESH);
  }
  getNativePrefs().then((p) => {
    if (!p) return;
    p.remove({ key: LS_ACCESS }).catch(() => {});
    p.remove({ key: LS_REFRESH }).catch(() => {});
  });
};

export const getStoredAccessToken = () =>
  (safeStorage && safeStorage.getItem(LS_ACCESS)) || null;

const getStoredRefreshToken = () =>
  (safeStorage && safeStorage.getItem(LS_REFRESH)) || null;

// One-shot rehydration on app boot — if localStorage is empty but
// native Preferences has tokens (e.g. fresh WebView session after an
// app upgrade), copy them over so the very first /auth/me call ships
// an Authorization header and the user stays logged in.
export const hydrateAuthTokensFromNative = async () => {
  if (!safeStorage) return false;
  if (safeStorage.getItem(LS_ACCESS)) return false; // already hydrated
  const p = await getNativePrefs();
  if (!p) return false;
  try {
    const [{ value: a }, { value: r }] = await Promise.all([
      p.get({ key: LS_ACCESS }),
      p.get({ key: LS_REFRESH }),
    ]);
    if (a) safeStorage.setItem(LS_ACCESS, a);
    if (r) safeStorage.setItem(LS_REFRESH, r);
    return !!(a || r);
  } catch (_) {
    return false;
  }
};

api.interceptors.request.use((config) => {
  const tok = getStoredAccessToken();
  if (tok && !config.headers?.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${tok}`;
  }
  // Attach device fingerprint header so the backend can record device
  // sessions and enforce fingerprint-level bans without a separate hop.
  try {
    const fp = typeof window !== "undefined" ? window.localStorage?.getItem("bidora.devfp.v1") : null;
    if (fp) {
      config.headers = config.headers || {};
      if (!config.headers["X-Bidora-Fingerprint"]) {
        config.headers["X-Bidora-Fingerprint"] = fp;
      }
    }
  } catch (_e) {
    /* localStorage may be disabled — skip silently */
  }
  return config;
});

// ─── Silent auto-refresh interceptor ──────────────────────────────────
// Whenever any request returns a 401 we'll fire a single /auth/refresh
// call (using the long-lived refresh_token cookie) and retry the original
// request. Lets users "stay logged in until they click logout" — once
// they're authenticated, expired access tokens are rotated transparently
// without ever bouncing them to the login page.
//
// Skips the refresh dance for the auth endpoints themselves (login,
// register, refresh, me, logout) so that genuinely-bad credentials
// surface their 401 to the UI immediately.
const NO_REFRESH_PATHS = [
  "/auth/login",
  "/auth/register",
  "/auth/refresh",
  "/auth/logout",
  "/auth/me",
  // OAuth code-exchange endpoints — never retry. A 401 here means the
  // code was rejected by Google, and retrying with the same (now spent)
  // code would just produce a second misleading 401.
  "/auth/google",
  "/auth/google-session",
];

let _refreshInflight = null;

api.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const original = error?.config;
    const status = error?.response?.status;
    if (status !== 401 || !original || original._retried) {
      return Promise.reject(error);
    }
    const url = original.url || "";
    if (NO_REFRESH_PATHS.some((p) => url.includes(p))) {
      return Promise.reject(error);
    }
    try {
      // Single in-flight refresh — multiple parallel 401s share the same
      // refresh promise so we don't hit /auth/refresh N times in a row.
      if (!_refreshInflight) {
        const rt = getStoredRefreshToken();
        _refreshInflight = api
          .post("/auth/refresh", rt ? { refresh_token: rt } : {})
          .then((r) => {
            // If the backend echoes a new access_token (mobile path),
            // persist it so the next request's Bearer header is fresh.
            const newAccess = r?.data?.access_token;
            if (newAccess && safeStorage) safeStorage.setItem(LS_ACCESS, newAccess);
            return r;
          })
          .finally(() => {
            _refreshInflight = null;
          });
      }
      await _refreshInflight;
      original._retried = true;
      // Re-apply the freshest Bearer header before retrying.
      const fresh = getStoredAccessToken();
      if (fresh) {
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${fresh}`;
      }
      return api(original);
    } catch (_) {
      return Promise.reject(error);
    }
  }
);

export function apiError(e) {
  // Network / CORS / offline — axios gives no `response`
  if (!e?.response) {
    const code = e?.code;
    const msg = e?.message || "";
    if (code === "ERR_NETWORK" || /network|failed to fetch/i.test(msg)) {
      return "Can't reach the server. Check your connection and try again.";
    }
    if (code === "ECONNABORTED" || /timeout/i.test(msg)) {
      return "Request timed out. Please try again.";
    }
    return msg ? `Connection error: ${msg}` : "Can't reach the server. Please try again.";
  }
  const detail = e.response?.data?.detail;
  if (detail == null) {
    const status = e.response.status;
    if (status === 401) return "Invalid credentials.";
    if (status === 403) return "You don't have access to this.";
    if (status === 404) return "Not found.";
    if (status >= 500) return `Server error (${status}). Please try again in a moment.`;
    return `Something went wrong (HTTP ${status}). Please try again.`;
  }
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => (d && typeof d.msg === "string" ? d.msg : JSON.stringify(d)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
